import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Save,
  Box,
  Mail,
  ShoppingBag,
  Check,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import usePermissions from '@/hooks/usePermissions';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PackagesPage = () => {
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = usePermissions();
  const canEdit = isAdmin || hasPermission('inventory.packages.edit');
  
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    package_type: 'box',
    length: '',
    width: '',
    height: '',
    dimension_unit: 'cm',
    weight: '',
    weight_unit: 'kg',
    is_default: false
  });

  const fetchPackages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/packages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const resetForm = () => {
    setFormData({
      name: '',
      package_type: 'box',
      length: '',
      width: '',
      height: '',
      dimension_unit: 'cm',
      weight: '',
      weight_unit: 'kg',
      is_default: false
    });
  };

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name || '',
        package_type: pkg.package_type || 'box',
        length: pkg.length || '',
        width: pkg.width || '',
        height: pkg.height || '',
        dimension_unit: pkg.dimension_unit || 'cm',
        weight: pkg.weight || '',
        weight_unit: pkg.weight_unit || 'kg',
        is_default: pkg.is_default || false
      });
    } else {
      setEditingPackage(null);
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a package name');
      return;
    }
    if (!formData.length || !formData.width || !formData.height) {
      toast.error('Please enter all dimensions');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingPackage) {
        await axios.put(
          `${API_BASE_URL}/couriers/packages/${editingPackage.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/couriers/packages`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package created');
      }
      
      setShowDialog(false);
      setEditingPackage(null);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/packages/${packageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Package deleted');
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete package');
    }
  };

  const getPackageIcon = (type) => {
    switch (type) {
      case 'envelope':
        return <Mail className="h-5 w-5" />;
      case 'soft_package':
        return <ShoppingBag className="h-5 w-5" />;
      default:
        return <Box className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-wt-bg/95 backdrop-blur-sm border-b border-wt-border">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-wt-text-muted hover:text-wt-text-primary"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-wt-text-primary flex items-center gap-2">
                <Package className="h-6 w-6 text-wt-gold" />
                Packages
              </h1>
              <p className="text-sm text-wt-text-muted">
                Manage shipping packages for your products
              </p>
            </div>
          </div>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Package
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {packages.length === 0 ? (
          <div className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center">
            <Package className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-50" />
            <h3 className="text-lg font-medium text-wt-text-primary mb-2">
              No Packages Created
            </h3>
            <p className="text-wt-text-muted mb-6">
              Create shipping packages to assign to your products for accurate courier booking
            </p>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Package
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-wt-surface border border-wt-border rounded-xl p-5 hover:border-wt-gold/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-wt-gold/20 flex items-center justify-center text-wt-gold">
                      {getPackageIcon(pkg.package_type)}
                    </div>
                    <div>
                      <h3 className="font-medium text-wt-text-primary">{pkg.name}</h3>
                      <p className="text-xs text-wt-text-muted capitalize">{pkg.package_type.replace('_', ' ')}</p>
                    </div>
                  </div>
                  {pkg.is_default && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      Default
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-wt-text-muted">Dimensions</span>
                    <span className="text-wt-text-primary font-mono">
                      {pkg.length} × {pkg.width} × {pkg.height} {pkg.dimension_unit}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-wt-text-muted">Weight (empty)</span>
                    <span className="text-wt-text-primary font-mono">
                      {pkg.weight} {pkg.weight_unit}
                    </span>
                  </div>
                  {pkg.shopify_package_id && (
                    <div className="flex items-center gap-1 text-xs text-green-400">
                      <Check className="h-3 w-3" />
                      Synced to Shopify
                    </div>
                  )}
                </div>

                {canEdit && (
                  <div className="flex items-center gap-2 pt-3 border-t border-wt-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(pkg)}
                      className="flex-1 text-wt-text-muted hover:text-wt-text-primary"
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(pkg.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Package className="h-5 w-5 text-wt-gold" />
              {editingPackage ? 'Edit Package' : 'Add Package'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Package Type */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Package Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'box', label: 'Box', icon: Box },
                  { value: 'envelope', label: 'Envelope', icon: Mail },
                  { value: 'soft_package', label: 'Soft Package', icon: ShoppingBag }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData(prev => ({ ...prev, package_type: type.value }))}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-colors ${
                      formData.package_type === type.value
                        ? 'border-wt-gold bg-wt-gold/10 text-wt-gold'
                        : 'border-wt-border text-wt-text-muted hover:border-wt-gold/50'
                    }`}
                  >
                    <type.icon className="h-5 w-5" />
                    <span className="text-xs">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Dimensions */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Dimensions</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Length"
                  value={formData.length}
                  onChange={(e) => setFormData(prev => ({ ...prev, length: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <span className="text-wt-text-muted">×</span>
                <Input
                  type="number"
                  placeholder="Width"
                  value={formData.width}
                  onChange={(e) => setFormData(prev => ({ ...prev, width: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <span className="text-wt-text-muted">×</span>
                <Input
                  type="number"
                  placeholder="Height"
                  value={formData.height}
                  onChange={(e) => setFormData(prev => ({ ...prev, height: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <Select
                  value={formData.dimension_unit}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, dimension_unit: v }))}
                >
                  <SelectTrigger className="w-20 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="cm">cm</SelectItem>
                    <SelectItem value="inches">in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Weight */}
            <div>
              <Label className="text-wt-text-primary mb-2 block">Weight (empty)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Weight"
                  value={formData.weight}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                  className="flex-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
                <Select
                  value={formData.weight_unit}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, weight_unit: v }))}
                >
                  <SelectTrigger className="w-20 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="gm">gm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Package Name */}
            <div>
              <Label className="text-wt-text-primary">Package Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Small Box, Large Envelope"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            {/* Default Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                className="rounded border-wt-border bg-wt-bg"
              />
              <label htmlFor="is_default" className="text-sm text-wt-text-secondary">
                Use as default package
              </label>
            </div>
            <p className="text-xs text-wt-text-muted -mt-2">
              Default package will be used when no specific package is assigned
            </p>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDialog(false);
                  setEditingPackage(null);
                }}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingPackage ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
